import React, { useState } from 'react';
import { PublicLayout } from '../../components/layout/PublicLayout';
import { ContestList } from '../../components/contest/ContestList';
import { ContestFilters } from '../../components/contest/ContestFilters';
import { Trophy, Search, Filter, Crown, Vote } from 'lucide-react';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { Link } from 'react-router-dom';
import { LazyImage } from '../../components/shared/LazyImage';
import { ContestVoteButton } from '../../components/contest/ContestVoteButton';
import { TopFiveGlobal } from '../../components/shared/TopFiveGlobal';

export const Contests: React.FC = () => {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');

  const { data: topContestants, isLoading: isLoadingTop } = useQuery({
    queryKey: ['topGlobalFive'],
    queryFn: async () => {
      // 1. Récupérer les concours actifs
      const { data: contests, error: contestsError } = await supabase
        .from('contests')
        .select('id, name, slug')
        .eq('status', 'active');

      if (contestsError) throw contestsError;

      // 2. Récupérer les candidats des concours actifs
      const { data: contestants, error: contestantsError } = await supabase
        .from('contestants')
        .select(`
          id,
          name,
          photo_url,
          unique_code,
          contest_id
        `)
        .in('contest_id', contests?.map(c => c.id) || [])
        .limit(50);

      if (contestantsError) throw contestantsError;
      if (!contestants) return [];

      // 3. Récupérer les votes validés pour chaque candidat
      const contestantsWithVotes = await Promise.all(contestants.map(async (contestant) => {
        const { count: votesCount } = await supabase
          .from('votes')
          .select(`
            id,
            transactions!inner (
              status
            )
          `, { count: 'exact', head: true })
          .eq('contestant_id', contestant.id)
          .eq('transactions.status', 'completed');

        const contest = contests?.find(c => c.id === contestant.contest_id);
        
        return {
          ...contestant,
          votes_count: votesCount || 0,
          contest_name: contest?.name || '',
          contest_slug: contest?.slug || ''
        };
      }));

      // 4. Trier et prendre les 5 meilleurs candidats
      return contestantsWithVotes
        .sort((a, b) => b.votes_count - a.votes_count)
        .slice(0, 5);
    },
    refetchInterval: 30000,
    staleTime: 15000,
    cacheTime: 60000,
  });

  return (
    <PublicLayout>
      <SEOMetaTags
        title="Concours ERNEST NZOGOUG - Participez aux Plus Grands Concours d'Afrique"
        description="Explorez les concours en cours sur ERNEST NZOGOUG. Miss, talents, sports, culture... Votez en ligne pour vos candidats préférés et participez à l'aventure africaine."
        image="https://nzofoug.com/contests-banner.jpg"
        url="https://nzofoug.com/contests"
      />
      <style jsx global>{`
        @keyframes shine {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }

        @keyframes text-shimmer {
          0% { background-position: -200% center; }
          100% { background-position: 200% center; }
        }
        
        .animate-text-shimmer {
          animation: text-shimmer 4s ease-in-out infinite;
          background-size: 200% auto;
        }

        .luxurious-card {
          position: relative;
          background: linear-gradient(to bottom right, rgba(30, 41, 59, 0.7), rgba(15, 23, 42, 0.7));
          backdrop-filter: blur(10px);
        }

        .luxurious-card::before {
          content: '';
          position: absolute;
          inset: -1px;
          background: linear-gradient(45deg, 
            rgba(234, 179, 8, 0.3),
            rgba(234, 179, 8, 0.1),
            rgba(234, 179, 8, 0.3)
          );
          background-size: 200% 200%;
          animation: shine 3s linear infinite;
          border-radius: inherit;
          z-index: -1;
        }

        @keyframes gradient-x {
          0% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
          100% {
            background-position: 0% 50%;
          }
        }
        .animate-gradient-x {
          background-size: 200% 100%;
          animation: gradient-x 4s ease infinite;
        }
      `}</style>

      <div className="min-h-screen relative">
        {/* Background Image avec Overlay */}
        <div 
          className="fixed inset-0 z-0"
          style={{
            backgroundImage: 'url(https://images.unsplash.com/photo-1492684223066-81342ee5ff30?q=80&w=1470)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-b from-black/95 via-black/80 to-black/95 backdrop-blur-[2px]"></div>
        </div>

        {/* Image de fond secondaire avec motif */}
        <div 
          className="fixed inset-0 z-0 opacity-10"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23fff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            backgroundSize: '30px 30px'
          }}
        ></div>

        {/* Contenu */}
        <div className="relative z-10 px-4 py-16 sm:py-24">
          <div className="max-w-7xl mx-auto backdrop-blur-md bg-black/30 rounded-3xl border border-white/10 shadow-2xl p-8 sm:p-12">
            {/* En-tête */}
            <div className="text-center mb-16 relative">
              <div className="flex justify-center mb-6">
                <div className="relative">
                  <div className="absolute -inset-4 bg-gradient-to-r from-yellow-600 via-yellow-400 to-yellow-600 rounded-full blur-lg opacity-50"></div>
                  <Trophy className="w-16 h-16 text-yellow-400 relative" />
                </div>
              </div>
              
              <h1 className="text-4xl sm:text-5xl font-extrabold mb-6">
                <span className="bg-gradient-to-r from-yellow-500 via-white to-yellow-500 text-transparent bg-clip-text animate-text-shimmer">
                  Explorez les Concours Étonnants
                </span>
              </h1>
              
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                Plongez dans des concours innovants et faites entendre votre voix à travers toute l'Afrique
              </p>
            </div>

            {/* Filtres */}
            <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 mb-12 shadow-lg">
              <div className="flex flex-col sm:flex-row gap-6 items-center justify-between">
                <div className="flex items-center space-x-2 text-yellow-400">
                  <Filter className="w-5 h-5" />
                  <span className="text-lg font-medium">Filtres</span>
                </div>
                <div className="flex-grow w-full sm:w-auto">
                  <ContestFilters
                    onFilterChange={setFilter}
                    onSearchChange={setSearch}
                  />
                </div>
              </div>
            </div>

            {/* Liste des concours */}
            <div className="relative mb-16">
              <div className="absolute inset-0 bg-gradient-to-b from-transparent via-yellow-400/5 to-transparent"></div>
              <ContestList
                filter={filter}
                search={search}
              />
            </div>

            {/* Top 5 Global */}
            <div className="mt-16">
              <div className="bg-white/5 backdrop-blur-sm rounded-lg border border-white/10 p-6">
                <TopFiveGlobal />
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
};